import React from 'react';
import { Box, Stack, Tooltip, IconButton, Typography, Switch, Slider, FormControlLabel } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * A reusable component to display a setting control (like Switch or Slider)
 * with a label and an optional info icon providing a preview/description via Tooltip.
 */
const SettingWithPreview = ({
    label,
    control,
    previewContent,
    tooltipPlacement = 'right',
    fullWidth = true, // Occupy full width by default
    labelProps = {}, // Allow passing props to label Typography
    controlContainerProps = {}, // Allow passing props to the Box containing the control
}) => {
    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: fullWidth ? '100%' : 'auto', py: 0.5 }} // Adjust padding as needed
        >
            {/* Label and Info Icon */}
            <Stack direction="row" alignItems="center" spacing={0.5}> {/* Reduced spacing */}
                <Typography variant="body1" component="span" {...labelProps}>{label}</Typography> {/* Changed to body1 */}
                {previewContent && (
                    <Tooltip title={previewContent} placement={tooltipPlacement} arrow>
                        {/* Make icon slightly smaller and adjust padding */}
                        <span>
                            <IconButton size="small" sx={{ p: 0.25 }}>
                                <InfoOutlinedIcon sx={{ fontSize: '1rem' }} color="action" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Stack>

            {/* The actual control */}
            <Box {...controlContainerProps}>
                {control}
            </Box>
        </Stack>
    );
};

/**
 * Helper component combining SettingWithPreview with a standard Switch control.
 */
export const SwitchSetting = ({ label, checked, onChange, previewContent, disabled = false, ...rest }) => (
    <SettingWithPreview
        label={label}
        previewContent={previewContent}
        control={
            <Switch
                checked={checked}
                onChange={onChange}
                size="small"
                disabled={disabled}
            />
        }
        {...rest}
    />
);

/**
 * Helper component combining SettingWithPreview with a standard Slider control.
 * Includes the value display next to the slider.
 * Tooltip/preview is intentionally removed for sliders based on feedback.
 */
export const SliderSetting = ({ label, value, onChange, disabled = false, min, max, step, marks, showValue = true, valueSuffix = '', ...rest }) => (
    <SettingWithPreview
        label={label}
        // previewContent={previewContent} // Removed preview content for sliders
        fullWidth={true} // Sliders usually need full width
        control={
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}> {/* Increased spacing */}
                <Slider
                    value={value}
                    onChange={onChange}
                    min={min}
                    max={max}
                    step={step}
                    marks={marks}
                    valueLabelDisplay="auto"
                    size="small"
                    disabled={disabled}
                    sx={{ flexGrow: 1, mr: showValue ? 1 : 0 }} // Ensure slider takes space
                />
                {showValue && (
                    <Typography variant="caption" sx={{ minWidth: '30px', textAlign: 'right' }}>
                        ({value?.toFixed(1)}{valueSuffix}) {/* Display value */}
                    </Typography>
                )}
            </Stack>
        }
        labelProps={{ variant: 'body2', color: disabled ? 'text.disabled' : 'text.primary' }} // Adjust label style for sliders
        controlContainerProps={{ sx: { width: '60%', pl: 2 } }} // Allocate more space for slider control
        {...rest}
    />
);


export default SettingWithPreview;
